import { AxiosError } from "axios";

export enum QueryOptions {
    SEARCH = "q",
    PAGE = "page"
}

export type TErrorMessage = AxiosError<{message: string}>

export type TSuccess<T> = (data: T) => void
export type TError = (error: TErrorMessage) => void

export type TApiResponse<T> = {
    results: T
    message?: string
    pagination?: TPagination
}

export type TLoginResponse = {
    token: string
    user: TUser
}

export type TPager = {
    page: number
    size: number
}

export type TPagination = {
    total: number
    last_page: number
    last: boolean
    first: boolean
    has_next: boolean
    has_previous: boolean
    current_page: number
    numberOfElements: number
    per_page: number
}

export type TRequestParams = {
    search?: string,
    pager?: TPager,
    filter?: Record<string, unknown>
}

export type TUser = {
    id: number
    firstName: string
    lastName: string
    username: string
    mobile: string
    email: string
    cover: string
    role?: TRoleMinimal
    phoneNumber: string;
    region: number;
    district: number;
    gender?: string;
    salutation: string;
    profileImage: string;
    nationality: string;
    identificationType: string;
    identificationNumber: string;
    dateOfBirth: string;
    identificationImages: string[];
    clientType: string;
    status: string;
    business: {
      id: number;
      businessName: string;
      businessTradingName: string;
      businessTinNumber: string;
      businessAddress: string;
      businessRegion: string;
      businessDistrict: string;
      businessStreet: string;
      businessPhone: string;
      businessEmail: string;
      businessManager: string;
      businessLat: string;
      businessLng: string;
      version: number;
    };
    licenses: {
      applicationId: string;
      businessName: string;
      physicalLocation: string;
      tin: string;
      status: string;
      licenseCategory: {
        id: number;
        categoryCode: string;
        name: string;
        subtitle: string;
        description: string;
        subcategories: {
          id: number;
          name: string;
          baseFee: number;
          percentageRate: number;
          calculationDays: number;
          managementFee: number;
          calculationFormula: string;
        }[];
      };
      subcategories: {
        id: number;
        name: string;
        baseFee: number;
        percentageRate: number;
        calculationDays: number;
        managementFee: number;
        calculationFormula: string;
      }[];
      rooms: {
        totalRoom: number;
        roomClass: string;
        rate: number;
      }[];
      shareholders: {
        shareholderName: string;
        shareholderRole: string;
      }[];
      facilityRestaurant: boolean;
      facilitySwimmingPool: boolean;
      facilityParking: boolean;
      facilityConferenceRooms: boolean;
      estimatedInvestmentAmount: number;
      sourceOfFunding: string;
      submittedDate: string;
      totalPaymentAmount: number;
      paymentStatus: string;
      paymentCalculation: {
        baseFee: number;
        managementFee: number;
        inspectionFee: number;
        totalAmount: number;
        roomPayments: {
          roomClass: string;
          numberOfRooms: number;
          ratePerRoom: number;
          totalAmount: number;
        }[];
        calculatedDate: string;
      };
      payments: {
        referenceNumber: string;
        applicationId: string;
        amount: number;
        paymentType: string;
        status: string;
        paymentDate: string;
        createdAt: string;
      }[];
      businessPlan: string;
      businessPlans: string;
      environmentalAssessment: string;
    }[];
    investments: {
      id: number;
      applicationId: string;
      businessDetails: {
        projectName: string;
        projectNumber: string;
        projectType: string;
        projectActivities: string[];
        numberOfRooms: number;
        dateIssued: string;
        describeTargetAudience: string;
        businessName: string;
        previousLicenseNumber: string;
        previousLicenseDateIssued: string;
        currentLicenseName: string;
        zone: string;
        region: string;
        district: string;
        street: string;
        postCode: string;
        poBox: string;
      };
      capitalInvestment: {
        companyOperatingProject: string;
        estimatedInvestmentAmount: number;
        currency: string;
        personalContribution: number;
        loanAmount: number;
        institutionProvidingLoan: string;
        bankName: string;
        bankAddress: string;
        shareholders: {
          salutation: string;
          firstName: string;
          middleName: string;
          lastName: string;
          nationality: string;
          numberOfShares: number;
        }[];
      };
      construction: {
        landSizeInHectares: number;
        existingBuildingsUsed: boolean;
        constructionCompletionTime: string;
        projectLifespanYears: number;
        projectLifespanYearsString: string;
        environmentalManagementPlans: string;
      };
      stakeholder: {
        salutation: string;
        firstName: string;
        middleName: string;
        lastName: string;
        nationality: string;
        numberOfShares: number;
      };
      workforce: {
        localPermanentEmployees: number;
        localTemporaryEmployees: number;
        foreignPermanentEmployees: number;
        foreignTemporaryEmployees: number;
        totalEmployees: number;
      };
      createdAt: string;
      updatedAt: string;
      applicationStatus: string;
      comment: string;
      tin: string;
      createdBy: string;
      status: string;
    }[];
    totalApplications: number;
    activeLicenses: number;
    totalInvestments: number;

}

export type TRoleMinimal = {
    id: number
    name: string
    description: string
    permissions: TPermission[]
}

export type TPermission = {
    id: number
    name: string
    group: string
    displayName: string
}

// Values are the query-string keys the API binds: LicenseFilterDTO /
// ApplicationFilterDTO expose searchTerm, status, startDate and endDate.
export enum FilterEnum {
    SEARCH = "searchTerm",
    STATUS = "status",
    TYPE = "type",
    START_DATE = "startDate",
    END_DATE = "endDate"
}



export type Stakeholder = {
    salutation: string
    firstName: string
    middleName: string
    lastName: string
    nationality: string
    numberOfShares: number
}

/**
 * GET /investment/{applicationId} — mirrors `ApplicationResponseDto` on the API.
 *
 * Only fields the API actually serialises are declared. The investment
 * application records no contact details (no e-mail, phone or contact person on
 * `BusinessDetails`) and no fee breakdown of its own — contact details come from
 * the applicant's account and the fees from constants/fees.ts.
 */
export type Investment = {
    id: number
    applicationId: string
    businessDetails: {
        projectName: string
        projectNumber: string
        projectType: string
        projectActivities: Array<string>
        numberOfRooms: number
        dateIssued: string
        describeTargetAudience: string
        businessName: string
        previousLicenseNumber: string
        previousLicenseDateIssued: string
        currentLicenseName: string
        zone: string
        region: string
        district: string
        street: string
        postCode: string
        poBox: string
    }
    capitalInvestment: {
        companyOperatingProject: string
        estimatedInvestmentAmount: number
        currency: string
        personalContribution: number
        loanAmount: number
        institutionProvidingLoan: string
        bankName: string
        bankAddress: string
        shareholders: Stakeholder[]
    }
    construction: {
        landSizeInHectares: number
        existingBuildingsUsed: boolean
        constructionCompletionTime: string
        projectLifespanYears: number
        environmentalManagementPlans: string
    }
    stakeholder: Stakeholder
    workforce: {
        localPermanentEmployees: number
        localTemporaryEmployees: number
        foreignPermanentEmployees: number
        foreignTemporaryEmployees: number
        totalEmployees: number
    }
    createdAt: string
    updatedAt: string
    applicationStatus: string
    /** The application's `statusComment` — the reviewer's reason for the current status. */
    comment?: string
    tin: string
    createdBy: string
    status: string
}



export type RequestOptions = {
    search?: string, 
    pager?: TPager, 
    filter?: Record<string, any>
}


export enum InspectionTypeEnum {
    REGULAR = "REGULAR",
    INVESTMENT = "INVESTMENT"
}

export enum InspectionStatusEnum {
    PENDING = "PENDING",
    IN_PROGRESS = "IN_PROGRESS",
    COMPLETED = "COMPLETED",
    CANCELLED = "CANCELLED"
}

export interface AreaOfInspection {
    name: string;
    passed?: boolean;
    comments?: string;
    findings: Finding[];
}
export interface Finding {
    id?: string;
    description?: string;
}
export interface TInspection {
    id?: number;  
    applicationId: string | undefined;  
    inspectorIds: string[];
    applicationType: string | null;  
    inspectorNames: string[];  
    type?: InspectionTypeEnum;  
    scheduledDate: string;
    completedDate: string | null; 
    status: InspectionStatusEnum | null;  
    location: string;
    description: string | undefined;  
    areasOfInspection: AreaOfInspection[];
    createdBy: string; 
    createdAt: string;
    updatedAt?: string;
    feedback: string | null;  
    violations: string[];  
    recommendations: string[]; 
}


export type TNotificationApplicationType = "LICENCE" | "INVESTMENT"

/**
 * In-app notification raised for the applicant when a reviewer moves one of
 * their applications forward. Mirrors `NotificationDTO` on the API.
 */
export type TNotification = {
    id: number
    applicationId: string
    applicationType: TNotificationApplicationType
    stage: string
    title: string
    message: string | null
    decisionStatus: string | null
    read: boolean
    createdAt: string
}
